import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Pagination, FormControl, Select, MenuItem, Stack,
  Card, CardContent, Grid, Alert, Switch, FormControlLabel, Tooltip, IconButton,
  LinearProgress, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  CircularProgress,
} from '@mui/material';
import {
  Payment, AccountBalance, CreditCard, TrendingUp, Receipt,
  CheckCircle, Error as ErrorIcon, Schedule, Autorenew, Info, Download,
  Visibility, Close, Person, School, Phone, Email, Home, CalendarMonth,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { paymentsAPI } from '../../api/client';
import { format } from 'date-fns';

const statusColors = {
  pending: 'warning', processing: 'info', completed: 'success',
  failed: 'error', refunded: 'default', partially_refunded: 'secondary',
};
const statusIcons = {
  pending: <Schedule fontSize="small" />, completed: <CheckCircle fontSize="small" />,
  failed: <ErrorIcon fontSize="small" />, refunded: <Autorenew fontSize="small" />,
};
const typeLabels = {
  rent: 'Rent', deposit: 'Deposit', service_fee: 'Service Fee',
  refund: 'Refund', penalty: 'Penalty',
};
const gatewayLogos = {
  paystack: '🟢 Paystack', stripe: '🟣 Stripe', manual: '📋 Manual',
};

function PaymentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [gatewayInfo, setGatewayInfo] = useState(null);
  const [verifyMsg, setVerifyMsg] = useState(null);
  const [stats, setStats] = useState({ total_paid: 0, pending_count: 0, completed_count: 0, recurring_count: 0 });
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 10 };
      if (statusFilter) params.status = statusFilter;
      const res = await paymentsAPI.list(params);
      const paymentList = res.data.payments || [];
      setPayments(paymentList);
      setTotal(res.data.total);

      // Compute quick stats from current page
      const completed = paymentList.filter(p => p.status === 'completed');
      const pending = paymentList.filter(p => p.status === 'pending');
      const recurring = paymentList.filter(p => p.is_recurring);
      setStats({
        total_paid: completed.reduce((s, p) => s + (p.amount || 0), 0),
        pending_count: pending.length,
        completed_count: completed.length,
        recurring_count: recurring.length,
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPayments(); }, [page, statusFilter]);

  useEffect(() => {
    paymentsAPI.gatewayInfo().then(r => setGatewayInfo(r.data)).catch(() => {});
  }, []);

  // Paystack verification: when user returns from Paystack, URL has ?reference=xxx or ?trxref=xxx
  useEffect(() => {
    const reference = searchParams.get('reference') || searchParams.get('trxref');
    if (!reference) return;

    const verifyPayment = async () => {
      try {
        const res = await paymentsAPI.paystackVerify(reference);
        if (res.data.status === 'success') {
          setVerifyMsg({ type: 'success', text: `Payment of GH₵ ${res.data.amount?.toLocaleString()} verified successfully! Your booking is now complete.` });
        } else {
          setVerifyMsg({ type: 'error', text: `Payment verification failed. Status: ${res.data.status}. Please contact support if you were charged.` });
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        setVerifyMsg({ type: 'error', text: typeof detail === 'string' ? detail : 'Payment verification failed. Please contact support.' });
      } finally {
        // Clean up URL params
        searchParams.delete('reference');
        searchParams.delete('trxref');
        searchParams.delete('verify');
        setSearchParams(searchParams, { replace: true });
        fetchPayments();
      }
    };
    verifyPayment();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleAutoBilling = async (paymentId, currentActive) => {
    try {
      await paymentsAPI.toggleAutoBilling(paymentId, { active: !currentActive });
      fetchPayments();
    } catch (err) { console.error(err); }
  };

  const handleViewReceipt = async (paymentId) => {
    setReceiptLoading(true);
    setReceiptOpen(true);
    try {
      const res = await paymentsAPI.getReceiptData(paymentId);
      setReceiptData(res.data);
    } catch (err) {
      console.error('Failed to load receipt:', err);
      setReceiptData(null);
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleDownloadReceipt = async (paymentId, transactionRef) => {
    try {
      const res = await paymentsAPI.downloadReceipt(paymentId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CampusNest_Receipt_${transactionRef}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Receipt download failed:', err);
    }
  };

  return (
    <Box>
      {verifyMsg && (
        <Alert severity={verifyMsg.type} sx={{ mb: 2 }} onClose={() => setVerifyMsg(null)}>
          {verifyMsg.text}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Payments</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage payments via {gatewayInfo?.available_gateways?.map(g => gatewayLogos[g] || g).join(' & ') || 'loading...'}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select value={statusFilter} displayEmpty onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="refunded">Refunded</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#e8f5e9' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <Box sx={{ bgcolor: '#4caf50', borderRadius: 2, p: 1.2, display: 'flex' }}><TrendingUp sx={{ color: '#fff' }} /></Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                <Typography variant="h6" fontWeight={700}>GHS {stats.total_paid.toLocaleString()}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#fff3e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <Box sx={{ bgcolor: '#ff9800', borderRadius: 2, p: 1.2, display: 'flex' }}><Schedule sx={{ color: '#fff' }} /></Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Pending</Typography>
                <Typography variant="h6" fontWeight={700}>{stats.pending_count}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <Box sx={{ bgcolor: '#2196f3', borderRadius: 2, p: 1.2, display: 'flex' }}><CheckCircle sx={{ color: '#fff' }} /></Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
                <Typography variant="h6" fontWeight={700}>{stats.completed_count}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#f3e5f5' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <Box sx={{ bgcolor: '#9c27b0', borderRadius: 2, p: 1.2, display: 'flex' }}><Autorenew sx={{ color: '#fff' }} /></Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Auto-billing</Typography>
                <Typography variant="h6" fontWeight={700}>{stats.recurring_count}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Supported Gateways Banner */}
      {gatewayInfo && (
        <Paper sx={{ px: 3, py: 1.5, mb: 3, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fafafa' }}>
          <Payment color="primary" />
          <Box flex={1}>
            <Typography variant="body2" fontWeight={600}>Payment Gateways</Typography>
            <Stack direction="row" spacing={1} mt={0.5}>
              {gatewayInfo.available_gateways?.map(g => (
                <Chip key={g} label={gatewayLogos[g] || g} size="small" variant="outlined"
                  sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Currencies</Typography>
            <Typography variant="body2" fontWeight={500}>
              {gatewayInfo.supported_currencies?.slice(0, 5).join(', ')}{gatewayInfo.supported_currencies?.length > 5 ? ` +${gatewayInfo.supported_currencies.length - 5}` : ''}
            </Typography>
          </Box>
        </Paper>
      )}

      {loading && <LinearProgress sx={{ mb: 1, borderRadius: 2 }} />}

      {/* Payments Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Reference</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Gateway</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Method</strong></TableCell>
              <TableCell><strong>Auto-billing</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Receipt</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Receipt sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                  <Typography color="text.secondary">No payments found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              payments.map((p) => (
                <TableRow key={p.id} hover sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                  <TableCell>
                    <Typography fontWeight={600} variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                      {p.transaction_ref}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={typeLabels[p.payment_type] || p.payment_type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>{p.currency} {p.amount?.toLocaleString()}</Typography>
                    {p.refund_amount > 0 && (
                      <Typography variant="caption" color="error" display="block">
                        Refund: {p.currency} {p.refund_amount?.toLocaleString()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={gatewayLogos[p.payment_gateway] || p.payment_gateway || '-'} size="small"
                      sx={{ fontWeight: 500, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell>
                    <Chip icon={statusIcons[p.status]} label={p.status?.replace('_', ' ')} size="small"
                      color={statusColors[p.status] || 'default'} sx={{ textTransform: 'capitalize', fontWeight: 500 }} />
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {p.payment_method?.replace('_', ' ') || '-'}
                  </TableCell>
                  <TableCell>
                    {p.is_recurring ? (
                      <Box>
                        <FormControlLabel
                          control={<Switch size="small" checked={p.auto_billing_active}
                            onChange={() => handleToggleAutoBilling(p.id, p.auto_billing_active)} color="success" />}
                          label={<Typography variant="caption">{p.recurring_interval}</Typography>}
                        />
                        {p.next_billing_date && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Next: {format(new Date(p.next_billing_date), 'dd MMM yyyy')}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {p.paid_at ? format(new Date(p.paid_at), 'dd MMM yyyy') : format(new Date(p.created_at), 'dd MMM yyyy')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.paid_at ? format(new Date(p.paid_at), 'HH:mm') : format(new Date(p.created_at), 'HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {p.status === 'completed' ? (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Receipt">
                          <IconButton size="small" color="primary" onClick={() => handleViewReceipt(p.id)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton size="small" color="success" onClick={() => handleDownloadReceipt(p.id, p.transaction_ref)}>
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {total > 10 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={Math.ceil(total / 10)} page={page} onChange={(e, v) => setPage(v)} color="primary" shape="rounded" />
        </Box>
      )}

      {/* Receipt Viewer Dialog */}
      <Dialog open={receiptOpen} onClose={() => { setReceiptOpen(false); setReceiptData(null); }} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        {receiptLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : receiptData ? (
          <>
            {/* Receipt Header */}
            <Box sx={{ bgcolor: '#1e40af', color: 'white', px: 3, py: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>CampusNest</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>Payment Receipt</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Chip label={receiptData.status} size="small"
                    sx={{ bgcolor: '#16a34a', color: 'white', fontWeight: 700, fontSize: '0.8rem' }} />
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
                    {receiptData.receipt_no}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <DialogContent sx={{ px: 3, py: 2 }}>
              {/* Transaction Info */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Date & Time</Typography>
                    <Typography variant="body2" fontWeight={600}>{receiptData.paid_at}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                    <Typography variant="body2" fontWeight={600}>{receiptData.payment_method}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Gateway</Typography>
                    <Typography variant="body2" fontWeight={600}>{receiptData.payment_gateway}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Booking Ref</Typography>
                    <Typography variant="body2" fontWeight={600}>{receiptData.booking_ref || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Tenant Info */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, color: '#1e40af' }}>
                      <Person fontSize="small" /> Tenant Information
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Name:</Typography>
                        <Typography variant="body2" fontWeight={600}>{receiptData.payer?.name}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Email:</Typography>
                        <Typography variant="body2">{receiptData.payer?.email}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Phone:</Typography>
                        <Typography variant="body2">{receiptData.payer?.phone}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Student ID:</Typography>
                        <Typography variant="body2" fontWeight={600}>{receiptData.payer?.student_id}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>University:</Typography>
                        <Typography variant="body2">{receiptData.payer?.university}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Property Info */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, color: '#1e40af' }}>
                      <Home fontSize="small" /> Accommodation Details
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Property:</Typography>
                        <Typography variant="body2" fontWeight={600}>{receiptData.property?.title}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Address:</Typography>
                        <Typography variant="body2">{receiptData.property?.address || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Room:</Typography>
                        <Typography variant="body2">{receiptData.property?.room_number || 'N/A'}{receiptData.property?.room_type ? ` (${receiptData.property.room_type})` : ''}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Landlord:</Typography>
                        <Typography variant="body2">{receiptData.landlord?.name}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Period:</Typography>
                        <Typography variant="body2">{receiptData.check_in && receiptData.check_out ? `${receiptData.check_in} - ${receiptData.check_out}` : 'N/A'}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              {/* Payment Breakdown */}
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ bgcolor: '#1e40af', px: 2, py: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="white">
                    <AccountBalanceWallet fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Payment Breakdown
                  </Typography>
                </Box>
                <Box sx={{ px: 2, py: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="body2">Accommodation Rent</Typography>
                    <Typography variant="body2" fontWeight={600}>{receiptData.currency} {receiptData.base_rent?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="body2">Platform Fee ({receiptData.platform_fee_pct}%)</Typography>
                    <Typography variant="body2" fontWeight={600}>{receiptData.currency} {receiptData.platform_fee?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, bgcolor: '#eff6ff', mx: -2, px: 2, mt: 1 }}>
                    <Typography variant="body1" fontWeight={700}>Total Paid</Typography>
                    <Typography variant="h6" fontWeight={700} color="#1e40af">
                      {receiptData.currency} {receiptData.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                This is a computer-generated receipt and does not require a signature.
              </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e5e7eb' }}>
              <Button onClick={() => { setReceiptOpen(false); setReceiptData(null); }} color="inherit" startIcon={<Close />}>
                Close
              </Button>
              <Button variant="contained" startIcon={<Download />}
                onClick={() => handleDownloadReceipt(receiptData.payment_id, receiptData.receipt_no)}>
                Download PDF
              </Button>
            </DialogActions>
          </>
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="error">Failed to load receipt data.</Typography>
            <Button onClick={() => { setReceiptOpen(false); setReceiptData(null); }} sx={{ mt: 2 }}>Close</Button>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}

export default PaymentsPage;
